import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validación básica
    if (!credentials.username || !credentials.password) {
      setError('Por favor completa todos los campos');
      setLoading(false);
      return;
    }

    try {
      // Guardar credenciales en localStorage
      localStorage.setItem('fronius_user', credentials.username);
      localStorage.setItem('fronius_auth', btoa(`${credentials.username}:${credentials.password}`));
      localStorage.setItem('is_authenticated', 'true');

      // Simular delay de autenticación
      await new Promise(resolve => setTimeout(resolve, 1200));

      navigate('/farmtrace/dashboard');
    } catch (err) {
      setError('Error al conectar con el sistema');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 flex items-center justify-center px-4 relative overflow-hidden">
      {/* Elementos decorativos animados */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-200/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* Formulario de login con efectos */}
        <div className="bg-white/98 backdrop-blur-xl rounded-3xl shadow-2xl p-8 transform hover:scale-[1.01] transition-all duration-300 border border-white/50">
          {/* Logo y título dentro del formulario */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center mb-6">
              <img
                src="/images/logo.png"
                alt="Logo"
                className="h-14 object-contain transform hover:scale-105 transition-transform duration-300"
              />
            </div>
            <p className="text-gray-500 text-sm">
              Sistema de Gestión Integral
            </p>
          </div>

          <div className="border-t border-gray-100 pt-6 mb-6"></div>

          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Bienvenido
            </h2>
            <p className="text-gray-500 text-sm">Ingresa tus credenciales para continuar</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Usuario con animación */}
            <div className="group">
              <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2">
                Usuario
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-all duration-300 group-focus-within:scale-110">
                  <i className="ri-user-line text-gray-400 group-focus-within:text-orange-500 text-xl"></i>
                </div>
                <input
                  id="username"
                  type="text"
                  placeholder="Ingresa tu usuario"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 text-sm font-medium hover:border-gray-300 bg-gray-50 focus:bg-white"
                />
              </div>
            </div>

            {/* Contraseña con toggle de visibilidad */}
            <div className="group">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-all duration-300 group-focus-within:scale-110">
                  <i className="ri-lock-line text-gray-400 group-focus-within:text-orange-500 text-xl"></i>
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ingresa tu contraseña"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 text-sm font-medium hover:border-gray-300 bg-gray-50 focus:bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer hover:scale-110 transition-transform"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 hover:text-gray-600 text-xl`}></i>
                </button>
              </div>
            </div>

            {/* Error message con animación */}
            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm animate-shake flex items-center space-x-2">
                <i className="ri-error-warning-line text-lg"></i>
                <span className="font-medium">{error}</span>
              </div>
            )}

            {/* Botón de submit con efectos */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold py-3.5 rounded-xl hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2 text-lg"></i>
                  Conectando al sistema...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <i className="ri-login-box-line mr-2 text-lg"></i>
                  Acceder al Dashboard
                </span>
              )}
            </button>
          </form>

          {/* Información adicional */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-xs text-gray-500">
              ¿Problemas para acceder?{' '}
              <a href="#" className="text-orange-600 hover:text-orange-700 font-semibold">
                Contactar soporte
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-gray-600 text-sm">
          <p className="flex items-center justify-center space-x-2">
            <i className="ri-copyright-line"></i>
            <span>2025 SolarF Energy Monitor. Todos los derechos reservados.</span>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }

        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
}
